import React from 'react';
import { MarketplaceFilters } from '../../../lib/marketplaceFilters';
import { PropertyAnchorRow } from './PropertyAnchorRow';
import { cn } from '../../../lib/utils';
import { SORT_OPTIONS } from '../../../lib/constants';
import { CaretDown } from '@phosphor-icons/react';
interface AdvancedFiltersPanelProps {
  filters: MarketplaceFilters;
  onChange: (filters: MarketplaceFilters) => void;
  properties: any[];
  selectedPropertyId: string | null;
  onSelectProperty: (id: string | null) => void;
  resultCount: number;
  onClose: () => void;
  onReset: () => void;
  onAddProperty?: () => void;
  isLoggedIn?: boolean;
  /** When true, render with mobile-drawer-friendly styling: tighter padding,
   *  single-column layout, and no built-in footer (the parent FilterPanel
   *  provides the sticky CTA in that case). */
  compact?: boolean;
}
// Local iOS-style switch — used for Verificări rows. The shared Toggle
// component is a button-style toggle (pressed/onPressedChange), which
// doesn't read as a switch in this context.
function Switch({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return <label className="flex items-center justify-between gap-4 cursor-pointer rounded-lg -mx-2 px-2 py-1.5 hover:bg-dark-3/40 transition-colors" onClick={e => {
    // Prevent double-fire when clicking on the inner button.
    if ((e.target as HTMLElement).closest('button[role="switch"]')) return;
    onChange(!checked);
  }} data-id="element-2292">
      <span className="text-sm text-light-2 select-none" data-id="element-2293">{label}</span>
      <button type="button" role="switch" aria-checked={checked} onClick={e => {
      e.stopPropagation();
      onChange(!checked);
    }} className={cn('relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-1 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-2', checked ? 'bg-lime-1' : 'bg-dark-4')} data-id="element-2294">
        <span aria-hidden="true" className={cn('pointer-events-none inline-block h-5 w-5 transform rounded-full bg-dark-1 shadow ring-0 transition-transform', checked ? 'translate-x-5' : 'translate-x-0')} data-id="element-2295" />
      </button>
    </label>;
}
export function AdvancedFiltersPanel({
  filters,
  onChange,
  properties,
  selectedPropertyId,
  onSelectProperty,
  resultCount,
  onClose,
  onReset,
  onAddProperty,
  isLoggedIn = false,
  compact = false
}: AdvancedFiltersPanelProps) {
  const toggleArray = (arr: string[], val: string) => {
    if (arr.includes(val)) return arr.filter(x => x !== val);
    return [...arr, val];
  };
  const update = (key: keyof MarketplaceFilters, val: any) => {
    onChange({
      ...filters,
      [key]: val
    });
  };
  return <div className={cn(compact ? 'p-4' : 'p-6 border-t border-dark-4 bg-dark-2 rounded-b-2xl')} data-id="element-2296">
      {/* Property Anchor — visible only for logged-in users. The selector itself
                  handles its own empty-state when the landlord has no properties yet. */}
      {isLoggedIn && <div className="mb-8" data-id="element-2297">
          <h4 className="text-sm font-medium text-light-1 mb-3" data-id="element-2298">
            Pentru ce proprietate cauți?
          </h4>
          <PropertyAnchorRow properties={properties} selectedId={selectedPropertyId} onSelect={onSelectProperty} onAddProperty={onAddProperty} data-id="element-2299" />
        </div>}

      <div className={cn('grid gap-x-12 gap-y-8', compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2')} data-id="element-2300">
        {/* Left Column */}
        <div className="space-y-8" data-id="element-2301">
          {/* Configurație */}
          <div data-id="element-2302">
            <h4 className="text-sm font-medium text-light-1 mb-3" data-id="element-2303">
              Configurație
            </h4>
            <div className="flex flex-wrap gap-2" data-id="element-2304">
              {[{
              val: 'singur',
              label: 'Singur'
            }, {
              val: 'cuplu',
              label: 'Cuplu'
            }, {
              val: 'familie',
              label: 'Familie'
            }, {
              val: 'combo',
              label: 'Combo-Rent'
            }].map(opt => <button key={opt.val} type="button" onClick={() => update('configuration', toggleArray(filters.configuration, opt.val))} className={cn('h-9 px-4 rounded-full text-sm font-medium transition-colors border cursor-pointer', filters.configuration.includes(opt.val) ? 'bg-lime-1/10 border-lime-1 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-light-4')} data-id="element-2305">
                  {opt.label}
                </button>)}
            </div>
          </div>

          {/* Lifestyle — hard gates only (reverse marketplace) */}
          <div data-id="element-2306">
            <h4 className="text-sm font-medium text-light-1 mb-3" data-id="element-2307">Lifestyle</h4>
            <div className="space-y-2" data-id="element-2308">
              <Switch checked={filters.noSmokers} onChange={c => update('noSmokers', c)} label="Doar nefumători" data-id="element-2309" />
              <Switch checked={filters.noPets} onChange={c => update('noPets', c)} label="Doar fără animale" data-id="element-2310" />
            </div>
          </div>

          {/* Durată */}
          <div data-id="element-2311">
            <h4 className="text-sm font-medium text-light-1 mb-3" data-id="element-2312">
              Durată contract
            </h4>
            <div className="flex flex-wrap gap-2" data-id="element-2313">
              {[{
              val: '1y',
              label: '1 an'
            }, {
              val: '2y',
              label: '2 ani'
            }, {
              val: 'short',
              label: 'Termen scurt'
            }, {
              val: 'indefinite',
              label: 'Nedeterminat'
            }].map(opt => <button key={opt.val} type="button" onClick={() => update('duration', toggleArray(filters.duration, opt.val))} className={cn('h-9 px-4 rounded-full text-sm font-medium transition-colors border cursor-pointer', filters.duration.includes(opt.val) ? 'bg-lime-1/10 border-lime-1 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-light-4')} data-id="element-2314">
                  {opt.label}
                </button>)}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8" data-id="element-2315">
          {/* Verificări */}
          <div data-id="element-2316">
            <h4 className="text-sm font-medium text-light-1 mb-3" data-id="element-2317">
              Verificări
            </h4>
            <div className="space-y-2" data-id="element-2318">
              <Switch checked={filters.verifiedOnly} onChange={c => update('verifiedOnly', c)} label="Doar profile verificate complet" data-id="element-2319" />
              <Switch checked={filters.incomeVerifiedOnly} onChange={c => update('incomeVerifiedOnly', c)} label="Doar cu venit verificat" data-id="element-2320" />
            </div>
          </div>

          {/* Rating */}
          <div data-id="element-2321">
            <div className="flex items-center justify-between mb-3" data-id="element-2322">
              <h4 className="text-sm font-medium text-light-1" data-id="element-2323">Rating minim</h4>
              <span className="text-sm text-lime-1 font-medium" data-id="element-2324">
                {filters.minRating > 0 ? `Cel puțin ${filters.minRating.toFixed(1)} ★` : 'Orice rating'}
              </span>
            </div>
            <input type="range" min="0" max="5" step="0.5" value={filters.minRating} onChange={e => update('minRating', parseFloat(e.target.value))} className="w-full accent-lime-1" data-id="element-2325" />
            <div className="flex justify-between text-xs text-light-4 mt-1" data-id="element-2326">
              <span data-id="element-2327">0</span>
              <span data-id="element-2328">5</span>
            </div>
          </div>

          {/* Sortare */}
          <div data-id="element-2329">
            <h4 className="text-sm font-medium text-light-1 mb-3" data-id="element-2330">Sortare</h4>
            <div className="relative" data-id="element-2331">
              <select value={filters.sort} onChange={e => update('sort', e.target.value)} className="w-full h-10 appearance-none rounded-xl border border-dark-4 bg-dark-3 px-4 pr-10 text-sm text-light-2 focus:border-lime-1 focus:ring-1 focus:ring-lime-1 transition-colors cursor-pointer" data-id="element-2332">
                {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value} disabled={opt.value === 'match' && !selectedPropertyId} data-id="element-2333">
                    {opt.label}{' '}
                    {opt.value === 'match' && !selectedPropertyId ? '(necesită selecție proprietate)' : ''}
                  </option>)}
              </select>
              <CaretDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-3 pointer-events-none" data-id="element-2334" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer — only when not in compact mode. The parent FilterPanel
            renders the sticky CTA on mobile. */}
      {!compact && <div className="flex items-center justify-between mt-8 pt-6 border-t border-dark-4" data-id="element-2335">
          <button type="button" onClick={onReset} className="text-sm text-light-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2336">
            Resetează filtrele
          </button>
          <button type="button" onClick={onClose} className="h-10 px-6 rounded-xl bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-2337">
            Arată {resultCount} chiriași
          </button>
        </div>}
    </div>;
}